import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vModelText as _vModelText, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, vModelRadio as _vModelRadio, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "w-full h-full px-4 xl:px-10 pt-6 xl:pt-[89px] pb-[20px] flex flex-col gap-4" }
const _hoisted_2 = { class: "flex max-md:flex-col gap-4 md:gap-x-10 md:gap-y-3 flex-wrap md:items-center" }
const _hoisted_3 = { class: "relative max-sm:w-full w-[233px] h-[52px] group" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "absolute left-0 right-0 bg-white border rounded mt-1 max-h-60 overflow-y-auto z-10 hidden group-focus-within:block"
}
const _hoisted_6 = ["onMousedown"]
const _hoisted_7 = ["placeholder"]
const _hoisted_8 = { class: "flex items-center gap-4" }
const _hoisted_9 = { class: "flex items-center gap-2" }
const _hoisted_10 = { class: "text-base leading-[20px] min-w-44 text-left" }
const _hoisted_11 = { class: "text-base leading-[20px]" }
const _hoisted_12 = {
  class: "flex gap-x-2",
  role: "group"
}
const _hoisted_13 = { class: "relative block w-9 h-9 rounded-lg border cursor-pointer" }
const _hoisted_14 = { class: "w-full h-full rounded-lg border border-muted p-2" }
const _hoisted_15 = { class: "relative w-full h-full rounded-[20px] z-0 overflow-hidden" }

import FacadeArrowImage from "@/views/Calculators/AzimuthFacade/components/FacadeArrowImage.vue";
import { computed, onMounted, ref, watch } from "vue";
import SanHeCircularImage from "@/views/Calculators/AzimuthFacade/components/SanHeCircularImage.vue";
import MountainsCircleImage from "@/views/Calculators/AzimuthFacade/components/MountainsCircleImage.vue";
import AnimalsCircleImage from "@/views/Calculators/AzimuthFacade/components/AnimalsCircleImage.vue";
import { useMap } from "@/utils/useMap";
import { useRotation } from "@/utils/useRotation";
import searchIcon from "@/assets/icons/search.svg";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import { useI18n } from "vue-i18n";
import { PlacePrediction } from "@/types/calculators";
import { CustomControl, GoogleMap } from "vue3-google-map";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import FlyingStarsControls from "@/views/Calculators/AzimuthFacade/components/FlyingStarsControls.vue";
import { useFlyingStars } from "@/utils/useFlyingStars";
import FlyingStarsComponent from "@/views/Calculators/AzimuthFacade/components/FlyingStarsComponent.vue";
import { parseCoordinates } from "@/utils/coordinates";


export default /*@__PURE__*/_defineComponent({
  __name: 'AzimuthFacadeView',
  setup(__props) {

const googleMapKey = process.env.VUE_APP_GOOGLE_MAP_KEY;
let timeout: number | null = null;

const arrowRef = ref<HTMLImageElement | null>(null);
const magneticCircleRef = ref<HTMLElement | null>(null);
const query = ref("");
const coordinateQuery = ref("");
const isCoordinatesValid = ref<boolean | null>(null);
const myMapRef = ref();
const selectedCircle = ref<"sanhe" | "mountains" | "animals">("mountains");
const selectedColor = ref("#FF0000");
const showStars = ref<boolean>(false);
const mapCenter = ref({ lat: 50.45, lng: 30.52 });

const circleComponent = computed(
  () =>
    ({
      sanhe: SanHeCircularImage,
      mountains: MountainsCircleImage,
      animals: AnimalsCircleImage,
    }[selectedCircle.value])
);
const displayAngle = computed(() => {
  const angle = rotateAngle.value - circleAngle.value;
  if (angle < 0) {
    return (angle + 360).toFixed(2);
  }
  return angle.toFixed(2);
});

const { circleAngle, endMoveMap } = useMap(magneticCircleRef);
const { rotateAngle, startRotation, isDragging } = useRotation(arrowRef);
const { options, fetchSuggestions, getGeocoder } = useGoogleAutocomplete(
  "ru",
  "address"
);
const { t } = useI18n();
const {
  fillYearStar,
  fillMonthStar,
  fillBuildYearStars,
  buildMountStars,
  azimuthFacade,
  flyingStars,
} = useFlyingStars();

onMounted(() => {
  getCurrentUserPosition();
});

function getCurrentUserPosition() {
  if (!navigator.geolocation) {
    console.warn("Geolocation is not supported.");
    return;
  }
  navigator.geolocation.getCurrentPosition(async (position) => {
    const { latitude, longitude } = position.coords;
    mapCenter.value = { lat: latitude, lng: longitude };
  });
}

watch(
  () => isDragging.value,
  (oldValue, newValue) => {
    if (!oldValue && newValue) {
      azimuthFacade.value = Number(displayAngle.value);
      buildMountStars();
    }
  }
);

function onInput() {
  if (timeout) clearTimeout(timeout);
  timeout = window.setTimeout(() => fetchSuggestions(query.value), 700);
}

async function centerMapBeCoordinates() {
  const coordinates = parseCoordinates(coordinateQuery.value);
  if (coordinates === null) {
    isCoordinatesValid.value = false;
  } else {
    isCoordinatesValid.value = true;
    myMapRef.value.map.setCenter(coordinates);
  }
}

async function selectOption(city: PlacePrediction) {
  query.value = city.label;
  try {
    const geocoder = await getGeocoder();
    if (geocoder) {
      await getGeocoderLocation(geocoder, city.label);
    } else {
      await getApproximatelyLocation(city);
    }
  } catch (e) {
    console.error("Error getting prediction place: ", e);
  }
}

async function getGeocoderLocation(
  geocoder: google.maps.Geocoder,
  label: string
) {
  const result = await geocoder.geocode({ address: label });
  if (!result || result.results.length < 0) {
    throw new Error("Geocode api didn't send a response");
  }

  const location = result.results[0].geometry.location;
  myMapRef.value.map.setCenter(location);
}

async function getApproximatelyLocation(city: PlacePrediction) {
  const place = city.place;
  await place.fetchFields({ fields: ["location"] });

  myMapRef.value.map.setCenter(place.location);
}

async function copyAngle() {
  try {
    await navigator.clipboard.writeText(displayAngle.value.toString());
  } catch (err) {
    console.error("Ошибка копирования:", err);
  }
}

function onIdle() {
  if (myMapRef.value !== undefined) {
    const center = myMapRef.value.map.getCenter();
    if (center && center.lat() && center.lng()) {
      endMoveMap(center.lat(), center.lng());
    }
  }
}

function handleBuildYearChange(year: number) {
  fillBuildYearStars(year);
}

function handleMonthStarsChange(year: number, month: number) {
  fillMonthStar(year, month);
}

function handleYearStarsChange(year: number, month: number) {
  fillYearStar(year);
  fillMonthStar(year, month);
}

function handleToggleShowStars(
  show: boolean,
  buildYear: number,
  yearStars: number,
  monthStars: number
) {
  if (show) {
    fillBuildYearStars(buildYear);
    fillYearStar(yearStars);
    fillMonthStar(yearStars, monthStars);
  }
  showStars.value = show;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("input", {
          type: "text",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
          onInput: onInput,
          placeholder: _unref(t)('calculators.azm.search-placeholder'),
          class: "w-full h-full input-shadow rounded-lg pl-[46px] pr-4 text-base leading-[20px] text-muted bg-no-repeat bg-[length:16px_16px] bg-[center_left_16px]",
          style: _normalizeStyle({ backgroundImage: `url(${_unref(searchIcon)})` })
        }, null, 44, _hoisted_4), [
          [_vModelText, query.value]
        ]),
        (_unref(options).length)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(options), (option) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: option.label,
                  onMousedown: ($event: any) => (selectOption(option)),
                  class: "px-3 py-2 hover:bg-gray-100 cursor-pointer"
                }, _toDisplayString(option.label), 41, _hoisted_6))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      _withDirectives(_createElementVNode("input", {
        type: "text",
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((coordinateQuery).value = $event)),
        onInput: centerMapBeCoordinates,
        placeholder: _unref(t)('calculators.azm.coordinate-search-placeholder'),
        class: _normalizeClass(["relative max-sm:w-full w-56 h-[52px] input-shadow rounded-lg px-4 text-base leading-[20px] text-muted outline-none", {
          'border border-success': isCoordinatesValid.value,
          'border border-error': isCoordinatesValid.value === false,
        }])
      }, null, 42, _hoisted_7), [
        [_vModelText, coordinateQuery.value]
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)("calculators.azm.facade-angle", {
                angle: displayAngle.value,
              })), 1),
          _createElementVNode("button", {
            class: "size-9 border border-brown rounded-lg",
            onClick: copyAngle
          }, [
            _createVNode(CopyIcon, { class: "text-black mx-auto" })
          ])
        ]),
        _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)("calculators.azm.magnetic-north", { angle: _unref(circleAngle) })), 1)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _withDirectives(_createElementVNode("input", {
          type: "radio",
          name: "circleType",
          id: "option1",
          value: "sanhe",
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedCircle).value = $event)),
          class: "sr-only peer"
        }, null, 512), [
          [_vModelRadio, selectedCircle.value]
        ]),
        _createElementVNode("label", {
          for: "option1",
          class: _normalizeClass(["p-2 rounded-lg cursor-pointer transition-colors border border-brown text-base leading-[20px]", 
            selectedCircle.value === 'sanhe'
              ? 'text-white bg-brown'
              : 'text-brown bg-transparent'
          ])
        }, " SanHe ", 2),
        _withDirectives(_createElementVNode("input", {
          type: "radio",
          name: "circleType",
          id: "option3",
          value: "animals",
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedCircle).value = $event)),
          class: "sr-only peer"
        }, null, 512), [
          [_vModelRadio, selectedCircle.value]
        ]),
        _createElementVNode("label", {
          for: "option3",
          class: _normalizeClass(["p-2 rounded-lg cursor-pointer transition-colors border border-brown text-base leading-[20px]", 
            selectedCircle.value === 'animals'
              ? 'text-white bg-brown'
              : 'text-brown bg-transparent'
          ])
        }, " Animals ", 2),
        _withDirectives(_createElementVNode("input", {
          type: "radio",
          name: "circleType",
          id: "option2",
          value: "mountains",
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedCircle).value = $event)),
          class: "sr-only peer"
        }, null, 512), [
          [_vModelRadio, selectedCircle.value]
        ]),
        _createElementVNode("label", {
          for: "option2",
          class: _normalizeClass(["p-2 rounded-lg cursor-pointer transition-colors border border-brown text-base leading-[20px]", 
            selectedCircle.value === 'mountains'
              ? 'text-white bg-brown'
              : 'text-brown bg-transparent'
          ])
        }, " Mountains ", 2)
      ]),
      _createElementVNode("label", _hoisted_13, [
        _withDirectives(_createElementVNode("input", {
          type: "color",
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedColor).value = $event)),
          class: "absolute inset-0 opacity-0 cursor-pointer w-full h-full"
        }, null, 512), [
          [_vModelText, selectedColor.value]
        ]),
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("div", {
            class: "w-full h-full rounded",
            style: _normalizeStyle({ backgroundColor: selectedColor.value })
          }, null, 4)
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      _createVNode(_unref(GoogleMap), {
        "api-key": _unref(googleMapKey),
        ref_key: "myMapRef",
        ref: myMapRef,
        center: mapCenter.value,
        zoom: 13,
        class: "w-full h-full",
        onIdle: onIdle,
        libraries: ['places'],
        "disable-default-ui": true,
        "zoom-control": true,
        "map-type-control": true,
        "map-type-control-options": {
          position: 20.0,
        }
      }, null, 8, ["api-key", "center"]),
      _createVNode(FlyingStarsControls, {
        "onUpdate:buildYear": handleBuildYearChange,
        "onUpdate:monthStars": handleMonthStarsChange,
        "onUpdate:yearStars": handleYearStarsChange,
        "onToggle:showStars": handleToggleShowStars
      }),
      _createElementVNode("div", {
        ref_key: "magneticCircleRef",
        ref: magneticCircleRef,
        class: "absolute top-1/2 left-1/2 z-[1000] translate-x-[-50%] translate-y-[-50%] pointer-events-none transition-transform duration-1000 ease-in-out w-full h-full p-2"
      }, [
        (_openBlock(), _createBlock(_resolveDynamicComponent(circleComponent.value), { color: selectedColor.value }, null, 8, ["color"]))
      ], 512),
      (showStars.value)
        ? (_openBlock(), _createBlock(FlyingStarsComponent, {
            key: 0,
            azimuthFacade: _unref(circleAngle),
            stars: _unref(flyingStars),
            color: selectedColor.value
          }, null, 8, ["azimuthFacade", "stars", "color"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        ref_key: "arrowRef",
        ref: arrowRef,
        class: "absolute top-1/2 left-1/2 z-[1000] translate-x-[-50%] translate-y-[-50%] cursor-grab select-none h-1/2",
        style: {"transform-origin":"center center"},
        onMousedown: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(startRotation) && _unref(startRotation)(...args))),
        onTouchstart: _cache[7] || (_cache[7] = 
//@ts-ignore
(...args) => (_unref(startRotation) && _unref(startRotation)(...args)))
      }, [
        _createVNode(FacadeArrowImage, { color: selectedColor.value }, null, 8, ["color"])
      ], 544)
    ])
  ]))
}
}

})